// Helper som bygger RelatedHubs-länkar för en city×hub-sida.
// Plockar ut format-longtails baserat på stadens överrepresenterade branscher,
// syskon-hubbar i samma stad, samt två jämförelsesidor som matchar typ av hubb.

import type { RelatedHubLink } from "@/components/site/landing/RelatedHubs";
import type { CityMeta } from "./cities";

export type HubKind =
  | "filmproduktion"
  | "reklamfilm"
  | "filmbyra"
  | "videobyra"
  | "produktionsbolag"
  | "videoproduktion"
  | "employer-branding-film";

const HUB_LABEL: Record<HubKind, string> = {
  filmproduktion: "Filmproduktion",
  reklamfilm: "Reklamfilm",
  filmbyra: "Filmbyrå",
  videobyra: "Videobyrå",
  produktionsbolag: "Produktionsbolag",
  videoproduktion: "Videoproduktion",
  "employer-branding-film": "Employer branding-film",
};

/** Format-longtails som passar de flesta städer. Kortlek vi väljer från. */
const FORMAT_POOL: RelatedHubLink[] = [
  { label: "Drönarfilm", path: "/dronarfilm", description: "Luftbilder och B-roll från ovan." },
  { label: "3D-animation", path: "/3d-animation", description: "När fysisk inspelning inte räcker." },
  { label: "Film för LinkedIn", path: "/film-for-linkedin", description: "Founder-led B2B-content." },
  { label: "Film för TikTok", path: "/film-for-tiktok", description: "Native vertikalt 9:16-format." },
  { label: "Film för YouTube", path: "/film-for-youtube", description: "Long-form och hub-content." },
  { label: "Film för Instagram", path: "/film-for-instagram", description: "Reels och Stories i skala." },
  { label: "Animerad reklamfilm", path: "/animerad-reklamfilm", description: "Snabba kampanjklipp." },
  { label: "Animerad förklaringsfilm", path: "/animerad-forklaringsfilm", description: "Förenkla komplexa erbjudanden." },
  { label: "Produktfilm", path: "/produktfilm", description: "Konvertera på sajt och paid social." },
  { label: "Employer branding-film", path: "/employer-branding-film", description: "Rekryterings­drivande content." },
  { label: "Kundcase-film", path: "/kundcase-film", description: "Social proof i video." },
  { label: "Eventfilm", path: "/eventfilm", description: "Aftermovies och liverapportering." },
  { label: "Rekryteringsfilm", path: "/rekryteringsfilm", description: "Rikta mot rätt kandidater." },
  { label: "Imagefilm", path: "/imagefilm", description: "Position och varumärke i en film." },
];

/** Industri → prioriterade format. Sortering baseras på stadens branscher. */
const INDUSTRY_FORMAT_PRIORITY: Record<string, string[]> = {
  SaaS: ["/film-for-linkedin", "/animerad-forklaringsfilm", "/produktfilm"],
  fintech: ["/film-for-linkedin", "/animerad-forklaringsfilm", "/imagefilm"],
  industri: ["/dronarfilm", "/kundcase-film", "/imagefilm"],
  tillverkning: ["/dronarfilm", "/kundcase-film", "/produktfilm"],
  fastighet: ["/dronarfilm", "/produktfilm", "/imagefilm"],
  "life science": ["/animerad-forklaringsfilm", "/3d-animation", "/imagefilm"],
  MedTech: ["/3d-animation", "/animerad-forklaringsfilm", "/film-for-linkedin"],
  deeptech: ["/3d-animation", "/animerad-forklaringsfilm", "/film-for-linkedin"],
  universitet: ["/rekryteringsfilm", "/imagefilm", "/employer-branding-film"],
  försvarsindustri: ["/3d-animation", "/imagefilm", "/film-for-linkedin"],
  tech: ["/film-for-linkedin", "/animerad-forklaringsfilm", "/produktfilm"],
  "B2B-tech": ["/film-for-linkedin", "/animerad-forklaringsfilm", "/produktfilm"],
  "tech-startups": ["/film-for-linkedin", "/animerad-forklaringsfilm", "/produktfilm"],
  "tech-scale-ups": ["/film-for-linkedin", "/produktfilm", "/animerad-forklaringsfilm"],
  konsumentvarumärken: ["/animerad-reklamfilm", "/film-for-tiktok", "/produktfilm"],
  FMCG: ["/animerad-reklamfilm", "/film-for-tiktok", "/produktfilm"],
  handel: ["/produktfilm", "/film-for-tiktok", "/animerad-reklamfilm"],
  "e-handel": ["/produktfilm", "/film-for-tiktok", "/animerad-reklamfilm"],
  logistik: ["/dronarfilm", "/imagefilm", "/kundcase-film"],
  energi: ["/dronarfilm", "/imagefilm", "/3d-animation"],
  "green tech": ["/dronarfilm", "/imagefilm", "/animerad-forklaringsfilm"],
  stål: ["/dronarfilm", "/imagefilm", "/kundcase-film"],
  datacenter: ["/dronarfilm", "/imagefilm", "/3d-animation"],
  skog: ["/dronarfilm", "/imagefilm", "/kundcase-film"],
  "papper och pappersmassa": ["/imagefilm", "/dronarfilm", "/kundcase-film"],
  "skog och energi": ["/dronarfilm", "/imagefilm", "/kundcase-film"],
  möbel: ["/produktfilm", "/imagefilm", "/film-for-instagram"],
  "möbel och inredning": ["/produktfilm", "/imagefilm", "/film-for-instagram"],
  "textil och mode": ["/produktfilm", "/film-for-tiktok", "/film-for-instagram"],
  turism: ["/dronarfilm", "/imagefilm", "/film-for-instagram"],
  "bank och försäkring": ["/imagefilm", "/film-for-linkedin", "/animerad-forklaringsfilm"],
  "offentlig sektor": ["/imagefilm", "/rekryteringsfilm", "/animerad-forklaringsfilm"],
  biotech: ["/3d-animation", "/animerad-forklaringsfilm", "/film-for-linkedin"],
  healthcare: ["/3d-animation", "/animerad-forklaringsfilm", "/imagefilm"],
  familjeföretag: ["/imagefilm", "/kundcase-film", "/employer-branding-film"],
  "kreativa näringar": ["/film-for-instagram", "/film-for-tiktok", "/aftermovie"],
};

/** Två jämförelsesidor som är relevanta för alla city×hub. */
const COMPARISONS: RelatedHubLink[] = [
  {
    label: "Filmbyrå vs frilansare",
    path: "/jamfor/filmbyra-vs-frilansare",
    description: "När det lönar sig att gå byrå.",
  },
  {
    label: "Inhouse vs filmbyrå",
    path: "/jamfor/inhouse-vs-filmbyra",
    description: "Bygga eget eller köpa kapacitet.",
  },
  {
    label: "Pulse vs Prime",
    path: "/jamfor/pulse-vs-prime",
    description: "Abonnemang eller projekt.",
  },
  {
    label: "Abonnemang vs projekt",
    path: "/jamfor/filmabonnemang-vs-projektfilm",
    description: "Hur ni får mest film per krona.",
  },
];

/**
 * Bygg RelatedHubs-länkar för en city×hub-sida.
 * - 3 format-longtails baserat på stadens branscher
 * - 2 syskon-hubbar i samma stad (andra tjänster)
 * - 2 jämförelsesidor (filmbyrå-vs-frilansare + 1 till)
 */
export function buildRelatedHubs(
  city: CityMeta,
  current: HubKind,
): {
  formats: RelatedHubLink[];
  siblings: RelatedHubLink[];
  comparisons: RelatedHubLink[];
} {
  // 1) Format — prioritera baserat på branscher, fyll på från generella pool
  const prioritized = new Set<string>();
  for (const industry of city.industries) {
    const formats = INDUSTRY_FORMAT_PRIORITY[industry];
    if (formats) formats.forEach((p) => prioritized.add(p));
  }

  const pickedFormats: RelatedHubLink[] = [];
  for (const path of prioritized) {
    const found = FORMAT_POOL.find((f) => f.path === path);
    if (found && pickedFormats.length < 3) pickedFormats.push(found);
  }
  // Fyll på från pool om vi inte fick 3
  for (const fmt of FORMAT_POOL) {
    if (pickedFormats.length >= 3) break;
    if (!pickedFormats.some((p) => p.path === fmt.path)) pickedFormats.push(fmt);
  }

  // 2) Syskon-hubbar i samma stad (2 st, beroende på vilken hubb vi är på)
  const SIBLING_ORDER: Record<HubKind, HubKind[]> = {
    filmproduktion: ["filmbyra", "videoproduktion"],
    reklamfilm: ["filmbyra", "produktionsbolag"],
    filmbyra: ["filmproduktion", "videobyra"],
    videobyra: ["filmproduktion", "filmbyra"],
    produktionsbolag: ["filmproduktion", "filmbyra"],
    videoproduktion: ["videobyra", "filmproduktion"],
    "employer-branding-film": ["filmproduktion", "videoproduktion"],
  };
  const siblings: RelatedHubLink[] = SIBLING_ORDER[current].map((sibling) => ({
    label: `${HUB_LABEL[sibling]} i ${city.name}`,
    path: `/${sibling}/${city.slug}`,
    description: `Samma stad — ${HUB_LABEL[sibling].toLowerCase()}-vinkel.`,
  }));

  // 3) Jämförelser — alltid filmbyra-vs-frilansare + en variant
  const comparisons: RelatedHubLink[] = [
    COMPARISONS[0],
    current === "filmbyra" || current === "produktionsbolag"
      ? COMPARISONS[1] // inhouse-vs-filmbyra
      : COMPARISONS[3], // abonnemang-vs-projekt
  ];

  return { formats: pickedFormats, siblings, comparisons };
}
